import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HistoryPage = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');

  const workspaceId = 'ws_demo';

  useEffect(() => {
    fetchPublications();
  }, [statusFilter, platformFilter]);

  const fetchPublications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (platformFilter !== 'all') params.append('platform', platformFilter);

      const response = await fetch(
        `${API_URL}/api/v1/workspaces/${workspaceId}/publications?${params}`
      );
      const data = await response.json();
      setPublications(data.publications || []);
    } catch (error) {
      console.error('Failed to fetch publications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { variant: 'secondary', icon: Calendar, label: 'Brouillon' },
      scheduled: { variant: 'default', icon: Clock, label: 'Programmé' },
      publishing: { variant: 'default', icon: Clock, label: 'En cours' },
      published: { variant: 'success', icon: CheckCircle, label: 'Publié' },
      partially_published: { variant: 'warning', icon: AlertCircle, label: 'Partiel' },
      failed: { variant: 'destructive', icon: XCircle, label: 'Échec' }
    };

    const { variant, icon: Icon, label } = config[status] || config.draft;

    return (
      <Badge variant={variant} className="flex items-center gap-1">
        <Icon size={14} />
        {label}
      </Badge>
    );
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      facebook: '📘',
      instagram: '📷',
      linkedin: '💼'
    };
    return icons[platform] || '🌐';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900" data-testid="history-page-title">Historique des Publications</h1>
          <p className="text-slate-600 mt-1">Consultez le statut et les résultats de vos publications</p>
        </div>

        {/* Filters */}
        <Card className="mb-6" data-testid="history-filters">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-48">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-md bg-white"
                  data-testid="status-filter"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="draft">Brouillon</option>
                  <option value="scheduled">Programmé</option>
                  <option value="published">Publié</option>
                  <option value="failed">Échec</option>
                </select>
              </div>
              <div className="w-full md:w-48">
                <select
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-md bg-white"
                  data-testid="platform-filter"
                >
                  <option value="all">Toutes les plateformes</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="linkedin">LinkedIn</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Publications List */}
        {loading ? (
          <div className="flex justify-center py-20" data-testid="history-loading">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : publications.length === 0 ? (
          <Card data-testid="history-empty">
            <CardContent className="py-20 text-center">
              <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Aucune publication</h3>
              <p className="text-slate-600">Vos publications apparaîtront ici</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="publications-list">
            {publications.map((pub) => (
              <Card key={pub.id} className="hover:shadow-md transition-shadow" data-testid={`publication-${pub.id}`}>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg">{pub.product_name || 'Produit'}</h3>
                          <p className="text-sm text-slate-500">
                            {new Date(pub.created_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                        {getStatusBadge(pub.status)}
                      </div>

                      <p className="text-slate-700 mb-4 line-clamp-2">{pub.content}</p>

                      {/* Platforms */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {pub.platforms.map(platform => (
                          <Badge key={platform} variant="outline" className="flex items-center gap-1">
                            <span>{getPlatformIcon(platform)}</span>
                            <span className="capitalize">{platform}</span>
                          </Badge>
                        ))}
                      </div>

                      {/* Results */}
                      {pub.results && pub.results.length > 0 && (
                        <div className="border-t border-slate-200 pt-3 mt-3">
                          <p className="text-sm font-medium text-slate-700 mb-2">Résultats par plateforme :</p>
                          <div className="space-y-1">
                            {pub.results.map((result, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm">
                                <span>{getPlatformIcon(result.platform)}</span>
                                <span className="capitalize">{result.platform}:</span>
                                {result.status === 'published' ? (
                                  <span className="text-green-600 flex items-center gap-1">
                                    <CheckCircle size={14} />
                                    Publié
                                  </span>
                                ) : (
                                  <span className="text-red-600 flex items-center gap-1">
                                    <XCircle size={14} />
                                    Échec: {result.error_message}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
